/**
 * lib/auth.ts — funciones puras de autenticación.
 *
 * Reciben el resultado crudo de apiFetch (ya validado por Zod) y devuelven
 * un discriminated union fácil de consumir en componentes.
 */
import { z } from 'zod';
import { apiFetch, ApiError } from '@/lib/api-client';
import {
  LoginRequestSchema,
  LoginResponseSchema,
  LoginTotpRequestSchema,
  ActivateRequestSchema,
  ForgotPasswordRequestSchema,
  ResetPasswordRequestSchema,
  MeResponseSchema,
  type MeResponse,
} from '@formacion-docente/schemas';

export type LoginOutcome =
  | { kind: 'ok'; actor: { id: string; email: string; nombre: string; apellido: string }; permisos: string[]; expiresIn: number }
  | { kind: 'totp_required'; tempToken: string; expiresIn: number };

export async function login(email: string, password: string): Promise<LoginOutcome> {
  const body = LoginRequestSchema.parse({ email, password });
  const res = await apiFetch('/auth/login', LoginResponseSchema, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (res.status === 'totp_required') {
    return { kind: 'totp_required', tempToken: res.temp_token, expiresIn: res.expires_in };
  }
  return { kind: 'ok', actor: res.actor, permisos: res.permisos, expiresIn: res.expires_in };
}

export async function loginTotp(tempToken: string, code: string): Promise<MeResponse> {
  const body = LoginTotpRequestSchema.parse({ temp_token: tempToken, code });
  const res = await apiFetch('/auth/login/totp', MeResponseSchema, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res;
}

export async function logout(): Promise<void> {
  await apiFetch('/auth/logout', z.unknown(), { method: 'POST' });
}

export async function fetchCurrentUser(): Promise<MeResponse | null> {
  try {
    return await apiFetch('/auth/me', MeResponseSchema);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export async function activateAccount(token: string, password: string): Promise<void> {
  const body = ActivateRequestSchema.parse({ token, password });
  await apiFetch('/auth/activate', z.unknown(), { method: 'POST', body: JSON.stringify(body) });
}

export async function requestPasswordReset(email: string): Promise<void> {
  const body = ForgotPasswordRequestSchema.parse({ email });
  await apiFetch('/auth/forgot-password', z.unknown(), { method: 'POST', body: JSON.stringify(body) });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  const body = ResetPasswordRequestSchema.parse({ token, password });
  await apiFetch('/auth/reset-password', z.unknown(), { method: 'POST', body: JSON.stringify(body) });
}